import React, { useState } from "react";
import { submitLeadQuietly } from "../lib/crm";
import { Link } from "react-router-dom";

// UniThink × SAS — AI & Data Science Landing Page
// React + TailwindCSS (plain JS)

// Identifies this form to the CRM: becomes the campaign name and the key its
// Lead Routing rule matches on.
const CRM_FORM_ID = "sas";

export default function SASLanding() {
  const SURVEY_URL = "https://forms.gle/uNkSobQxVfaHtCEx7"; 

  // --- Role Explorer data (from screenshots) ---
  const roles = [
    {
      key: "programmer",
      label: "Programmer / Clinical Trials Programmer",
      blurb:
        "If you're a tech‑savvy problem solver, programming is for you. Write code to access, prepare and analyze data. Take the first step toward roles in clinical trials, data science, machine learning and AI with SAS training.",
      salary: 127400,
      growth: "60.4%",
      jobs: 6904,
    },
    {
      key: "datascientist",
      label: "Data Scientist",
      blurb:
        "Turn data into insights. Work with structured and unstructured data, build and test AI/ML models with SAS, and share your work with technical and non‑technical audiences.",
      salary: 127900,
      growth: "78.8%",
      jobs: 76523,
    },
    {
      key: "mlengineer",
      label: "Machine Learning Engineer",
      blurb:
        "Enter the high‑demand field of AI and automation. Build and deploy models with strong governance, solve business problems, and gain an in‑depth understanding of the analytics life cycle with SAS.",
      salary: 140200,
      growth: "71.1%",
      jobs: 21794,
    },
    {
      key: "administrator",
      label: "Administrator",
      blurb:
        "Install and configure SAS software, monitor system performance, stay current with SAS updates, and support users by managing access and solving issues.",
      salary: 87500,
      growth: "7.2%",
      jobs: 23768,
    },
    {
      key: "businessanalyst",
      label: "Business Analyst",
      blurb:
        "Uncover opportunities hidden in data. Build UI‑based analytics and machine learning, and create dashboards that deliver better intelligence across your organization.",
      salary: 92600,
      growth: "27.6%",
      jobs: 242051,
    },
  ];

  const [activeRole, setActiveRole] = useState(0);

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    city: "",
    background: "",
    interest: "Data & AI (GenAI / Analytics)",
    consent: false,
    message: "",
  });
  const [loading, setLoading] = useState(false);


  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [gotcha, setGotcha] = useState(""); // bot trap; kept out of the Sheet payload

  const handleChange = (e) => {
  const { name, value, type, checked } = e.target;
  setForm({
    ...form,
    [name]: type === "checkbox" ? checked : value,
  });
};

// handleSubmit (fixing setSubmitted + reset)
const handleSubmit = async (e) => {
  e.preventDefault();
  const errs = validate();
  setErrors(errs);
  if (Object.keys(errs).length > 0) return;

  try {
    // Double-writing while the CRM proves itself: the Sheet is still the
    // source of truth, so only its outcome decides what the visitor sees.
    const [sheetResult] = await Promise.allSettled([
      fetch("https://script.google.com/macros/s/AKfycbzTqu_FRYCNYuzgI3XDy41HpOCt_XgMsW2hUY9qphXnGsgmZrhLPrihqdrDkTZZMpn4/exec", {
        method: "POST",
        mode: "no-cors",
        body: JSON.stringify(form),
        headers: { "Content-Type": "application/json" },
      }),
      submitLeadQuietly({ formId: CRM_FORM_ID, fields: { ...form, _gotcha: gotcha } }),
    ]);
    if (sheetResult.status === "rejected") throw sheetResult.reason;
    setSubmitted(true);
    setForm({
      fullName: "",
      email: "",
      phone: "",
      city: "",
      interest: "Data & AI (GenAI / Analytics)",
      message: "",
      consent: false,
    });
  } catch (err) {
    alert("Error submitting form");
  }
};

  const validate = () => {
    const errs = {};
    if (!form.fullName.trim()) errs.fullName = "Please add your full name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(form.email)) errs.email = "Enter a valid email";
    if (!/^[0-9+\-()\s]{8,}$/i.test(form.phone)) errs.phone = "Enter a valid phone number";
    if (!form.city.trim()) errs.city = "Your city helps us connect you to the right counselor";
    if (!form.consent) errs.consent = "Please allow us to contact you";
    return errs;
  };

  

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50 text-slate-900">
      {/* Sticky Top Bar */}
      <header className="sticky top-0 z-40 backdrop-blur bg-white/70 border-b border-slate-200">
  <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
    
    {/* Logo (left) */}
    <img
      src="/UniThink x SAS logo.png"
      alt="SAS Logo"
      className="h-30 w-36"
    />

    {/* Enquire Button (right) */}
    <a
      href="#enquiry"
      className="hidden sm:inline-flex px-4 py-2 rounded-2xl shadow-sm bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
    >
      Enquire Now
    </a>
  </div>
</header>


      {/* Hero */}
      <section>
        <div className="max-w-6xl mx-auto px-4 py-12 lg:py-16 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            
            <h1 className="mt-4 text-3xl md:text-4xl font-extrabold tracking-tight">Launch your <span className="text-blue-700">AI & Data</span> career with <span className="whitespace-nowrap">SAS®️ </span></h1>
            <p className="mt-4 text-slate-700 leading-relaxed">UniThink is now an official SAS Education Partner in India. Get industry‑ready with hands‑on learning paths, live classes, and globally recognized SAS certifications in Data & AI and Clinical Programming.</p>
            <ul className="mt-6 space-y-2 text-sm text-slate-700">
              <li className="flex items-start gap-3"><span className="mt-1 h-5 w-5 rounded-full bg-green-100 text-green-800 flex items-center justify-center text-xs">✓</span>Structured learning paths curated by SAS experts</li>
              <li className="flex items-start gap-3"><span className="mt-1 h-5 w-5 rounded-full bg-green-100 text-green-800 flex items-center justify-center text-xs">✓</span>Digital training, live classes, virtual labs & mentorship</li>
              <li className="flex items-start gap-3"><span className="mt-1 h-5 w-5 rounded-full bg-green-100 text-green-800 flex items-center justify-center text-xs">✓</span>Preparation for SAS credential exams with digital badges</li>
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="#programs" className="px-5 py-3 rounded-2xl bg-slate-900 text-white hover:bg-slate-800 font-medium shadow">Explore Programs</a>
              <a href="#enquiry" className="px-5 py-3 rounded-2xl bg-white ring-1 ring-slate-200 hover:ring-slate-300 font-medium shadow-sm">Enquire Now</a>
            </div>
          </div>
          <div
  className="relative aspect-[4/3] rounded-3xl ring-1 ring-slate-200 shadow-inner flex items-center justify-center bg-cover bg-center"
  style={{ backgroundImage: "url('/sas_img.jpeg')" }} // image from /public folder
>
  {/* optional overlay for readability */}
  <div className="absolute inset-0 bg-black/30 rounded-3xl"></div>

</div>
        </div>
      </section>

      {/* Programs */}
      <section id="programs" className="max-w-6xl mx-auto px-4 py-10">
        <h2 className="text-2xl md:text-3xl font-bold">Choose your SAS learning path</h2>
        <p className="mt-2 text-slate-600">Two job‑aligned tracks powered by SAS. Click a card to view detailed program highlights on a dedicated page.</p>

        <div className="mt-8 grid md:grid-cols-2 gap-6">
          {/* Card 1: Data & AI */}
          

<Link 
  to="/sas/data-ai" 
  className="group block rounded-3xl ring-1 ring-slate-200 bg-white hover:shadow-xl hover:-translate-y-0.5 transition-all p-6"
>
  


          
            <div className="flex items-start gap-4">
              
              <div>
                <h3 className="text-xl font-semibold group-hover:text-blue-700">SAS Academy for Data & AI Excellence</h3>
                <p className="mt-2 text-sm text-slate-700">Master analytics, machine learning, and GenAI workflows using SAS tools. Learn data manipulation, statistical analysis, model building, MLOps basics and visualization.</p>
                <ul className="mt-3 text-sm text-slate-600 list-disc pl-5 space-y-1">
                  <li>Digital learning + live classes + virtual labs</li>
                  <li>Projects with real‑world datasets</li>
                  <li>Preparation for SAS certification exams</li>
                </ul>
                <p className="mt-3 inline-flex items-center text-sm font-medium text-blue-700">View program highlights →</p>
              </div>
            </div>
          </Link>

          {/* Card 2: Clinical Programming */}
          <a href="/sas/clinical-programming" className="group block rounded-3xl ring-1 ring-slate-200 bg-white hover:shadow-xl hover:-translate-y-0.5 transition-all p-6">
            <div className="flex items-start gap-4">
              
              <div>
                <h3 className="text-xl font-semibold group-hover:text-emerald-700">SAS Academy for Clinical Programming</h3>
                <p className="mt-2 text-sm text-slate-700">Build expertise for roles in clinical research and biostatistics. Learn standards‑driven data management, analysis and reporting for clinical trials using SAS.</p>
                <ul className="mt-3 text-sm text-slate-600 list-disc pl-5 space-y-1">
                  <li>Case‑based exercises & demo datasets</li>
                  <li>Good Clinical Practice (GCP) aligned workflows</li>
                  <li>Certification preparation & career guidance</li>
                </ul>
                <p className="mt-3 inline-flex items-center text-sm font-medium text-emerald-700">View program highlights →</p>
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* --- Role Explorer Section (moved up) --- 
      <section className="bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-6xl mx-auto px-4 py-14">
          <h2 className="text-2xl md:text-3xl font-bold text-center">Explore SAS training tailor‑made for you</h2>

          {/* Role pills 
          <div className="mt-8 flex gap-4 overflow-x-auto pb-2 snap-x">
            {roles.map((r, idx) => (
              <button
                key={r.key}
                onClick={() => setActiveRole(idx)}
                className={`shrink-0 snap-start px-4 py-2 rounded-full ring-1 text-sm font-medium transition ${
                  activeRole === idx
                    ? "bg-blue-600 text-white ring-blue-600"
                    : "bg-white text-slate-700 ring-slate-200 hover:ring-slate-300"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>

          {/* Active role card 
          <div className="mt-8 rounded-3xl ring-1 ring-slate-200 bg-white p-6 md:p-8 shadow-sm">
            <h3 className="text-xl md:text-2xl font-semibold text-center">{roles[activeRole].label}</h3>
            <p className="mt-3 text-slate-700 text-center max-w-3xl mx-auto">{roles[activeRole].blurb}</p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
              <Stat label="average salary" value={`$${roles[activeRole].salary.toLocaleString()}`} />
              <Stat label="projected job growth" value={roles[activeRole].growth} />
              <Stat label="jobs in 2024" value={roles[activeRole].jobs.toLocaleString()} />
            </div>

            <p className="mt-6 text-center text-xs text-slate-500">Data from Lightcast, 2023. Represents US only.</p>
          </div>
        </div>
      </section>*/}

      {/* --- Learning Formats Section (moved below Role Explorer) --- */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-14">
          <h2 className="text-2xl md:text-3xl font-bold text-center">Training for any way you want to learn</h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
  {[
    {
      title: "Digital training",
      desc: "Curated courses, hands-on labs and certification prep designed to build specific skills and earn a credential.",
    },
    {
      title: "Live classes",
      desc: "Ask questions and interact with an instructor during online or in-person classes.",
    },
    {
      title: "Private Training and Mentoring",
      desc: "Schedule private training for your team and hire a SAS instructor for on-the-job coaching.",
    },
    {
      title: "Video tutorials",
      desc: "Strengthen your skills with hundreds of free videos developed by SAS instructors.",
    },
  ].map((f, i) => (
    <div
      key={i}
      className="rounded-3xl ring-1 ring-slate-200 bg-slate-50 p-6"
    >
      <div className="h-10 w-10 rounded-lg bg-blue-100 grid place-content-center text-blue-700 font-bold">
        {i + 1}
      </div>
      <h3 className="mt-4 text-lg font-semibold">{f.title}</h3>
      <p className="mt-2 text-sm text-slate-700 leading-relaxed">
        {f.desc}
      </p>
    </div>
  ))}
</div>

          
        </div>
      </section>

      {/* Why SAS x UniThink */}
      <section className="bg-white/60 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-3 gap-6">
          {[
            {
              title: "Learn in-demand data skills",
              desc: "Structured courses, hands-on labs and live sessions designed by SAS to help you master analytics and AI workflows.",
            },
            {
              title: "Earn recognized credentials",
              desc: "Add SAS certifications to your résumé and share verifiable digital badges with employers.",
            },
            {
              title: "Career support by UniThink",
              desc: "Guidance on learning paths, exam prep, and profile building for internships and jobs.",
            },
          ].map((f, i) => (
            <div key={i} className="rounded-3xl ring-1 ring-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-lg font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Enquiry Form */}
      <section id="enquiry" className="max-w-3xl mx-auto px-4 py-12">
        <div className="rounded-3xl ring-1 ring-slate-200 bg-white p-6 md:p-8 shadow-xl">
          <h2 className="text-2xl font-bold">Enquire Now</h2>
          <p className="mt-2 text-slate-600 text-sm">Tell us a bit about you and the program you’re interested in. Our counselor will reach out with details on schedules, fees, and next steps.</p>

          {submitted ? (
            <div className="mt-6 rounded-2xl bg-green-50 ring-1 ring-green-200 p-4">
              <p className="font-medium text-green-800">Thank you! Your enquiry has been received.</p>
              <p className="text-sm text-green-800/80">We’ll contact you shortly on the provided phone/email.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Bot trap: hidden from people, so anything typed here is spam. */}
              <input
                type="text"
                name="_gotcha"
                value={gotcha}
                onChange={(e) => setGotcha(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
              />
              <div>
                <label className="text-sm font-medium">Full Name *</label>
                <input name="fullName" value={form.fullName} onChange={handleChange} className={`mt-1 w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 ${errors.fullName ? "border-red-300 ring-red-100" : "border-slate-300 ring-blue-100"}`} placeholder="Your full name" />
                {errors.fullName ? <p className="text-xs text-red-600 mt-1">{errors.fullName}</p> : null}
              </div>
              <div>
                <label className="text-sm font-medium">Email *</label>
                <input name="email" value={form.email} onChange={handleChange} className={`mt-1 w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 ${errors.email ? "border-red-300 ring-red-100" : "border-slate-300 ring-blue-100"}`} placeholder="you@example.com" />
                {errors.email ? <p className="text-xs text-red-600 mt-1">{errors.email}</p> : null}
              </div>
              <div>
                <label className="text-sm font-medium">Phone / WhatsApp *</label>
                <input name="phone" value={form.phone} onChange={handleChange} className={`mt-1 w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 ${errors.phone ? "border-red-300 ring-red-100" : "border-slate-300 ring-blue-100"}`} placeholder="+91 …" />
                {errors.phone ? <p className="text-xs text-red-600 mt-1">{errors.phone}</p> : null}
              </div>
              <div>
                <label className="text-sm font-medium">City *</label>
                <input name="city" value={form.city} onChange={handleChange} className={`mt-1 w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 ${errors.city ? "border-red-300 ring-red-100" : "border-slate-300 ring-blue-100"}`} placeholder="Your city" />
                {errors.city ? <p className="text-xs text-red-600 mt-1">{errors.city}</p> : null}
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium">Your Background (degree / experience)</label>
                <input name="background" value={form.background} onChange={handleChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100" placeholder="e.g., B.Tech CSE, 1 year as Analyst" />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium">Interested Program</label>
                <select name="interest" value={form.interest} onChange={handleChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100">
                  <option>Data & AI (GenAI / Analytics)</option>
                  <option>Clinical Programming (Clinical Trials)</option>
                  <option>Unsure — please guide me</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium">Questions / Notes</label>
                <textarea name="message" value={form.message} onChange={handleChange} rows={4} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100" placeholder="Any specific questions (fees, schedule, certification, etc.)" />
              </div>
              <div className="md:col-span-2 flex items-start gap-3">
                <input id="consent" type="checkbox" name="consent" checked={form.consent} onChange={handleChange} className="mt-1 h-4 w-4 rounded border-slate-300" />
                <label htmlFor="consent" className="text-sm text-slate-700">I agree to be contacted by UniThink about SAS programs via phone/WhatsApp/email.</label>
              </div>
              {errors.consent ? <p className="md:col-span-2 text-xs text-red-600 -mt-2">{errors.consent}</p> : null}

              <div className="md:col-span-2 flex gap-3">
                <button type="submit" className="px-5 py-3 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-medium shadow">Submit Enquiry</button>
                <a href="#programs" className="px-5 py-3 rounded-2xl bg-white ring-1 ring-slate-200 hover:ring-slate-300 font-medium shadow-sm">Back to Programs</a>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Survey Section */}
      <section className="bg-gradient-to-r from-blue-50 to-blue-100 border-y border-slate-200 py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold">Not sure where to get started?</h2>
          <p className="mt-3 text-slate-700">Take a quick survey to help us match you with the right SAS course based on your background, skills and career goals.</p>
          <a href={SURVEY_URL} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex px-5 py-3 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-medium shadow">Take Survey</a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-8 text-sm text-slate-600 grid md:grid-cols-2 gap-4">
          <p>©️ {new Date().getFullYear()} UniThink Education International Pvt. Ltd. All rights reserved.</p>
          <p className="md:text-right">UniThink — Your Trusted SAS®️ Education Partner.</p>
        </div>
      </footer>
    </div>
  );
}

// Small presentational component for stats
function Stat({ label, value }) {
  return (
    <div className="rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-5 text-center">
      <div className="text-xl font-semibold">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-wide text-slate-500">{label}</div>
    </div>
  );
}